import { Controller } from "@hotwired/stimulus"

/**
 * Tag Creator Controller
 *
 * Mini-formulaire inline pour creer un tag depuis le formulaire de notes.
 * POST JSON vers l'endpoint note_tags, puis insere le tag comme checkbox coche.
 */
export default class extends Controller {
  static targets = ["form", "nameInput", "tagList", "error"]

  static values = {
    url: String,
    colorMap: Object
  }

  // === Lifecycle ===

  connect() {
    this.selectedColor = "violet"
  }

  // === Actions ===

  toggleForm(event) {
    event.preventDefault()
    if (!this.hasFormTarget) return

    const isHidden = this.formTarget.classList.toggle("hidden")
    event.currentTarget.setAttribute("aria-expanded", (!isHidden).toString())

    if (!isHidden && this.hasNameInputTarget) {
      this.nameInputTarget.focus()
    }
  }

  selectColor(event) {
    event.preventDefault()
    const color = event.currentTarget.dataset.color
    if (!color) return

    this.selectedColor = color

    // Mettre a jour le ring visuel sur toutes les pastilles
    this.formTarget.querySelectorAll("[data-color]").forEach((dot) => {
      dot.classList.toggle("ring-2", dot.dataset.color === color)
      dot.classList.toggle("ring-white/60", dot.dataset.color === color)
      dot.classList.toggle("scale-125", dot.dataset.color === color)
    })
  }

  async createTag(event) {
    event.preventDefault()
    if (!this.hasNameInputTarget) return

    const name = this.nameInputTarget.value.trim()
    if (!name) {
      this.showError(this.nameInputTarget.dataset.requiredMessage || "Nom requis")
      return
    }

    this.hideError()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          student_note_tag: { name, color: this.selectedColor }
        })
      })

      if (!response.ok) {
        const data = await response.json()
        this.showError(data.errors?.[0] || "Erreur lors de la creation")
        return
      }

      const tag = await response.json()
      this.appendTag(tag)
      this.nameInputTarget.value = ""
    } catch (error) {
      console.error("Tag creation error:", error)
      this.showError("Erreur de connexion")
    }
  }

  submitOnEnter(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.createTag(event)
    }
  }

  // === Helpers ===

  appendTag({ id, name, color }) {
    if (!this.hasTagListTarget) return

    const formClasses = this.colorMapValue[color] || "bg-slate-500/15 text-slate-400 border-slate-500/30"

    const label = document.createElement("label")
    label.className = "cursor-pointer"

    const checkbox = document.createElement("input")
    checkbox.type = "checkbox"
    checkbox.name = "student_note[note_tag_ids][]"
    checkbox.value = id
    checkbox.className = "sr-only peer"
    checkbox.checked = true

    const span = document.createElement("span")
    span.className = `inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-all duration-200 ${formClasses} peer-checked:ring-2 peer-checked:ring-white/50`
    span.textContent = name

    label.appendChild(checkbox)
    label.appendChild(span)
    this.tagListTarget.appendChild(label)
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
